'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  MessageSquare,
  CalendarDays,
  Star,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  LogOut,
  UserCircle,
  ClipboardList,
  ShieldCheck,
  Menu,
  X,
} from 'lucide-react';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const workerNav: NavItem[] = [
  { label: 'Dashboard', href: '/worker-dashboard', icon: <LayoutDashboard size={20} /> },
  { label: 'Messages', href: '/worker-messages', icon: <MessageSquare size={20} />, badge: 2 },
  { label: 'Booking Requests', href: '/worker-dashboard', icon: <ClipboardList size={20} />, badge: 1 },
  { label: 'Availability & Calendar', href: '/worker-dashboard', icon: <CalendarDays size={20} /> },
  { label: 'My Reviews', href: '/worker-dashboard', icon: <Star size={20} /> },
  { label: 'Vetting Docs', href: '/worker-dashboard', icon: <ShieldCheck size={20} /> },
  { label: 'Settings', href: '/worker-dashboard', icon: <Settings size={20} /> },
];

const employerNav: NavItem[] = [
  { label: 'Dashboard', href: '/employer-dashboard', icon: <LayoutDashboard size={20} /> },
  { label: 'Messages', href: '/messages', icon: <MessageSquare size={20} />, badge: 3 },
  { label: 'My Bookings', href: '/employer-dashboard', icon: <CalendarDays size={20} /> },
  { label: 'Reviews', href: '/employer-dashboard', icon: <Star size={20} /> },
  { label: 'Settings', href: '/employer-dashboard', icon: <Settings size={20} /> },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
  role: 'worker' | 'employer';
  userName: string;
}

export default function DashboardLayout({ children, role, userName }: DashboardLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const navItems = role === 'worker' ? workerNav : employerNav;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-2 p-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-extrabold text-base tracking-tight" style={{ color: 'var(--navy)' }}>
            Handlenders
          </span>
        )}
      </div>

      {/* Role badge */}
      {!collapsed && (
        <div className="px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
            {role === 'worker' ? 'Candidate Portal' : 'Employer Portal'}
          </span>
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 px-2 py-2 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = pathname === item.href;
          return (
            <Link
              key={`sidebar-${item.href}-${item.label}`}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group relative ${
                isActive
                  ? 'bg-orange-pale text-primary' :'text-foreground hover:bg-muted hover:text-primary'
              }`}
            >
              <span className="flex-shrink-0">{item.icon}</span>
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {!collapsed && item.badge && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: 'var(--primary)', minWidth: '20px', textAlign: 'center' }}>
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: 'var(--primary)' }} />
              )}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-border">
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--orange-pale)' }}>
            <UserCircle size={20} style={{ color: 'var(--primary)' }} />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{userName}</p>
              <p className="text-xs capitalize" style={{ color: 'var(--muted-foreground)' }}>{role}</p>
            </div>
          )}
          {!collapsed && (
            <Link href="/sign-up-login" title="Sign out" className="p-1.5 rounded-lg hover:bg-muted transition-colors">
              <LogOut size={16} style={{ color: 'var(--muted-foreground)' }} />
            </Link>
          )}
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden lg:flex items-center justify-center p-2 mx-3 mb-3 rounded-xl border border-border hover:bg-muted transition-all duration-150"
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </div>
  );

  return (
    <div className="flex h-screen bg-muted overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-border transition-all duration-300 ease-in-out flex-shrink-0 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-10 w-64 bg-white h-full flex flex-col animate-slide-up">
            <button className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-muted" onClick={() => setMobileOpen(false)}>
              <X size={18} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="bg-white border-b border-border px-4 lg:px-6 h-14 flex items-center justify-between flex-shrink-0">
          <button className="lg:hidden p-2 rounded-lg hover:bg-muted" onClick={() => setMobileOpen(true)}>
            <Menu size={20} />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <button className="relative p-2 rounded-lg hover:bg-muted transition-colors">
              <Bell size={18} style={{ color: 'var(--muted-foreground)' }} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: 'var(--primary)' }} />
            </button>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--orange-pale)' }}>
              <UserCircle size={20} style={{ color: 'var(--primary)' }} />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 xl:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}